"use client";
import { useEffect, useRef, useState } from "react";
import TabsBar from "@/components/ui/Tabs";
import Collapse from "@/components/ui/Collapse";
import AllInformation from "./_information";
import Questions from "./_questions";
import Comments from "@/components/Comments";
import { lockNavbarReveal } from "@/components/layout/Header/useScrollDirection";

export const SCROLL_TO_TAB_EVENT = "single-product:scroll-to-tab";

// بخشی که بالایش تا این فاصله زیر نوار تب رسیده باشد فعال است؛ اسکرولِ کلیک روی تب بخش را دقیقا زیر نوار می‌نشاند
const ACTIVE_LINE_OFFSET = 24;

export const scroll_to_product_tab = (id) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(SCROLL_TO_TAB_EVENT, { detail: { id } }));
};

const Tabs = ({ description, review = "", information, comments, questions, variant = "desktop" }) => {

    const getSectionId = (base) => `${base}-${variant}`;

    /* هر ref جداگانه گرفته می‌شود و مستقیم به `ref=` پاس داده می‌شود؛ پاس دادنِ
       `sectionRefs.x` داخل JSX را قانونِ react-hooks/refs به‌عنوان خواندنِ ref
       در زمان رندر می‌گیرد و وارنینگ می‌دهد. `sectionRefs` فقط برای پیدا کردنِ
       ref از روی id در افکت‌ها و اسکرول می‌ماند. */
    const informationRef = useRef(null);
    const descriptionRef = useRef(null);
    const reviewRef = useRef(null);
    const commentsRef = useRef(null);
    const questionsRef = useRef(null);

    const sectionRefs = {
        information: informationRef,
        description: descriptionRef,
        review: reviewRef,
        comments: commentsRef,
        questions: questionsRef,
    };

    const tabs = [
        { id: "information", label: "مشخصات", show: information?.attribute_groups?.length > 0 },
        { id: "description", label: "معرفی", show: description.length > 0 },
        { id: "review", label: "بررسی تخصصی", show: review.length > 0 },
        { id: "comments", label: "نظرات کاربران", show: true },
        { id: "questions", label: "پرسش‌ها", show: true },
    ];

    const visibleTabs = tabs.filter((tab) => tab.show);

    const [activeTab, setActiveTab] = useState(visibleTabs?.[0]?.id || "comments");

    const tabsBarRef = useRef(null);
    const wrapperRef = useRef(null);
    const isProgrammaticScroll = useRef(false);
    const programmaticScrollTimeout = useRef(null);

    const getStickyOffset = () => {
        const header_height =
            parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue("--header-height")
            ) || 0;
        const tabs_bar_height = tabsBarRef.current?.offsetHeight || 0;

        return header_height + tabs_bar_height;
    };

    const settleScroll = (el, attempt = 0) => {
        const delta = el.getBoundingClientRect().top - getStickyOffset();

        if (Math.abs(delta) > 2 && attempt < 3) {
            window.scrollBy({ top: delta, behavior: "auto" });
            programmaticScrollTimeout.current = setTimeout(
                () => settleScroll(el, attempt + 1),
                120
            );
            return;
        }

        isProgrammaticScroll.current = false;
    };

    /* مدت اسکرول نرم مرورگر به طول مسیر بستگی دارد و بعد از باز شدن Collapse مسیر بلندتر است؛
       با زمان ثابت، اصلاح وسط انیمیشن انجام می‌شد و صفحه یک‌باره می‌پرید. */
    const settleAfterScrollStops = (el, last_y = null) => {
        programmaticScrollTimeout.current = setTimeout(() => {
            const y = window.scrollY;
            if (y !== last_y) {
                settleAfterScrollStops(el, y);
                return;
            }
            settleScroll(el, 0);
        }, 150);
    };

    const scrollToSection = (id) => {
        const el = sectionRefs[id]?.current;
        if (!el) return;

        isProgrammaticScroll.current = true;
        clearTimeout(programmaticScrollTimeout.current);

        const top = window.scrollY + el.getBoundingClientRect().top - getStickyOffset();
        window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });

        settleAfterScrollStops(el);
    };

    const handleTabClick = (id) => {
        setActiveTab(id);
        scrollToSection(id);
    };

    const sectionClass = "scroll-mt-[140px]";

    /* تب فعال از جای واقعی بخش‌ها حساب می‌شود: آخرین بخشی که بالایش به زیر نوار تب رسیده.
       قبلا IntersectionObserver فقط ورود بخش به نوار ۲۰ تا ۴۰ درصد صفحه را می‌دید؛ وقتی Collapse
       یک بخش را بلند می‌کرد، آن بخش هیچ‌وقت از نوار خارج نمی‌شد و با اسکرول به بالا تب روی
       بخش بعدی گیر می‌کرد. ResizeObserver همان تغییر ارتفاع (باز و بسته شدن Collapse) را می‌گیرد. */
    useEffect(() => {
        let frame = 0;

        const update = () => {
            frame = 0;
            if (isProgrammaticScroll.current) return;
            if (!wrapperRef.current || wrapperRef.current.offsetParent === null) return;

            const line =
                (tabsBarRef.current?.getBoundingClientRect().bottom ?? getStickyOffset()) +
                ACTIVE_LINE_OFFSET;

            let current = null;
            Object.entries(sectionRefs).forEach(([id, ref]) => {
                const el = ref.current;
                if (!el) return;
                if (!current || el.getBoundingClientRect().top <= line) current = id;
            });

            if (current) setActiveTab(current);
        };

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };

        const resizeObserver = new ResizeObserver(schedule);
        if (wrapperRef.current) resizeObserver.observe(wrapperRef.current);
        window.addEventListener("scroll", schedule, { passive: true });

        return () => {
            clearTimeout(programmaticScrollTimeout.current);
            if (frame) cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            window.removeEventListener("scroll", schedule);
        };
    }, []);

    /* وقتی نوار تب زیر هدر چسبیده و نشانگر روی آن است، اسکرول به بالا ردیف دوم هدر را باز نمی‌کند؛
       وگرنه تب‌ها ۵۷ پیکسل پایین می‌روند، دسته‌بندی محصولات زیر نشانگر می‌آید و تب‌ها از دست می‌روند. */
    useEffect(() => {
        const pointer = { x: null, y: null };
        let locked = false;

        const update = () => {
            const bar = tabsBarRef.current;
            let next = false;

            if (bar && pointer.x !== null && wrapperRef.current?.offsetParent !== null) {
                const rect = bar.getBoundingClientRect();
                const header_height =
                    parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
                const pinned = rect.top <= header_height + 1;
                const over =
                    pointer.x >= rect.left && pointer.x <= rect.right &&
                    pointer.y >= rect.top && pointer.y <= rect.bottom;
                next = pinned && over;
            }

            if (next !== locked) {
                locked = next;
                lockNavbarReveal(next);
            }
        };

        const handlePointer = (event) => {
            pointer.x = event.clientX;
            pointer.y = event.clientY;
            update();
        };

        const handleLeave = () => {
            pointer.x = null;
            pointer.y = null;
            update();
        };

        document.addEventListener("mousemove", handlePointer, { passive: true });
        document.documentElement.addEventListener("mouseleave", handleLeave);
        // در فاز capture تا قفل قبل از ارزیابی هدر (در requestAnimationFrame) ست شده باشد
        window.addEventListener("scroll", update, { passive: true, capture: true });

        return () => {
            document.removeEventListener("mousemove", handlePointer);
            document.documentElement.removeEventListener("mouseleave", handleLeave);
            window.removeEventListener("scroll", update, { capture: true });
            if (locked) lockNavbarReveal(false);
        };
    }, []);

    useEffect(() => {
        const handler = (event) => {
            const id = event?.detail?.id;
            if (!id || !sectionRefs[id]?.current) return;
            if (!wrapperRef.current || wrapperRef.current.offsetParent === null) return;

            setActiveTab(id);
            scrollToSection(id);
        };

        window.addEventListener(SCROLL_TO_TAB_EVENT, handler);
        return () => window.removeEventListener(SCROLL_TO_TAB_EVENT, handler);
    }, []);

    return (
        <>
            {/* Sticky Tabs */}
            <div className="w-full" ref={wrapperRef}>
                <div
                    ref={tabsBarRef}
                    className="sticky w-full top-[var(--header-height,48px)] bg-jmt-white z-10">
                    {/* تب گلوبال پروژه */}
                    <TabsBar
                        items={visibleTabs.map((tab) => ({ id: tab.id, label: tab.label }))}
                        value={activeTab}
                        onChange={handleTabClick}
                        scrollerClassName="px-4 md:px-10 lg:px-0"
                        listClassName="gap-8"
                        tabClassName="p-4 text-sm"
                        inactiveClassName="text-jmt-neutral-500 hover:text-jmt-neutral-800"
                    />
                </div>


                <div className="pt-4 lg:pt-6 space-y-5 lg:space-y-6">

                    {/* information */}
                    {information?.attribute_groups?.length > 0 ? <section
                        id={getSectionId("information")}
                        data-tab-id="information"
                        ref={informationRef}
                        className={sectionClass}>
                        <div className="w-full px-4 md:px-10 lg:px-0">
                            <h2 className="font-bold text-base lg:text-xl text-jmt-main pb-4">مشخصات</h2>
                            <div className="pb-5 lg:pb-6">
                                <Collapse
                                    collapsedHeight={180}
                                    expandText="مشاهده بیشتر"
                                    collapseText="بستن"
                                    expandButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                    collapseButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                    animationDuration={300}
                                >
                                    <AllInformation data={information} />
                                </Collapse>
                            </div>
                        </div>
                        <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2" />
                    </section> : ""}

                    {/* Description */}
                    {description.length > 0 ? <section
                        id={getSectionId("description")}
                        data-tab-id="description"
                        ref={descriptionRef}
                        className={sectionClass}>
                        <div className="w-full px-4 md:px-10 lg:px-0">
                            <h2 className="font-bold text-base lg:text-xl text-jmt-main pb-4">معرفی</h2>
                            <div className="pb-5 lg:pb-6">
                                <Collapse
                                    collapsedHeight={180}
                                    expandText="مشاهده بیشتر"
                                    collapseText="بستن"
                                    expandButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                    collapseButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                >
                                    <div
                                        className="text-editor-single-products"
                                        dangerouslySetInnerHTML={{ __html: description }}
                                    />
                                </Collapse>
                            </div>
                        </div>
                        <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2 " />
                    </section> : ""}

                    {/* Review - متنِ بلندِ تحریریه، پس collapse بازترى می‌گیرد */}
                    {review.length > 0 ? <section
                        id={getSectionId("review")}
                        data-tab-id="review"
                        ref={reviewRef}
                        className={sectionClass}>
                        <div className="w-full px-4 md:px-10 lg:px-0">
                            <h2 className="font-bold text-base lg:text-xl text-jmt-main pb-4">بررسی تخصصی</h2>
                            <div className="pb-5 lg:pb-6">
                                <Collapse
                                    collapsedHeight={320}
                                    expandText="مشاهده بیشتر"
                                    collapseText="بستن"
                                    expandButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                    collapseButtonClassName="text-jmt-primary-800 text-xs font-bold mt-4"
                                >
                                    <div
                                        className="text-editor-single-products"
                                        dangerouslySetInnerHTML={{ __html: review }}
                                    />
                                </Collapse>
                            </div>
                        </div>
                        <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2 " />
                    </section> : ""}

                    {/* Comments */}
                    <section
                        id={getSectionId("comments")}
                        data-tab-id="comments"
                        ref={commentsRef}
                        className={sectionClass}>
                        <div className="w-full px-4 md:px-10 lg:px-0 pb-5 lg:pb-6">
                            <Comments data={comments} />
                        </div>
                        <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2" />
                    </section>

                    {/* Questions */}
                    <section
                        id={getSectionId("questions")}
                        data-tab-id="questions"
                        ref={questionsRef}
                        className={sectionClass}>
                        <div className="w-full px-4 md:px-10 lg:px-0 pb-5 lg:pb-6">
                            <Questions data={questions} />
                        </div>
                    </section>

                </div>
            </div>
        </>
    );
};

export default Tabs;

